// src/components/EmptyState.js
// データ0件のときに「このタブは何をする場所か」を説明するオンボーディング用の空状態。
// 各タブは <EmptyState preset="invoices" onAction={openNew} /> の1行で使える。
// preset を使わず icon/title/desc/bullets/actionLabel を直接渡してもOK。

import React from 'react';
import { useTheme } from '../lib/ThemeContext';
import {
  IconDashboard, IconClients, IconProjects, IconExpenses,
  IconInvoices, IconAnalytics, IconAi, IconWorkProof,
} from '../lib/icons';

// ── タブごとの説明プリセット ──
const PRESETS = {
  dashboard: {
    icon: IconDashboard, color: '#6366f1',
    title: 'Totonoeeへようこそ',
    desc: 'ここは今月の収支・未請求・依頼をひと目で見るホームです。まずはプロフィールを設定すると、請求書や稼働証明書に情報が自動で反映されます。',
    bullets: ['表示名・振込先・署名を登録', '請求書PDFに自動で記載される', '電子印を稼働証明書に押印'],
    actionLabel: 'プロフィールを設定する',
  },
  clients: {
    icon: IconClients, color: '#7dd3fc',
    title: 'クライアントを登録しましょう',
    desc: '取引先の会社情報を登録すると、案件や請求書にワンタップで紐づきます。案件数・請求総額・入金率もここでまとめて確認できます。',
    bullets: ['会社名・担当者・連絡先を登録', '案件と請求書が自動でひも付く', '入金状況をまとめて把握'],
    actionLabel: '最初のクライアントを登録',
  },
  projects: {
    icon: IconProjects, color: '#6366f1',
    title: '案件を登録して管理しましょう',
    desc: '進行中の仕事を登録すると、商談中→受注→進行中→請求済み→完了のステータスで管理できます。納品・時給・単発・月契約の4タイプに対応。',
    bullets: ['ステータスで進捗を一目で管理', '時給・単発はカレンダーで稼働日を記録', '案件カードから経費も追加できる'],
    actionLabel: '最初の案件を追加',
  },
  expenses: {
    icon: IconExpenses, color: '#f59e0b',
    title: '経費を記録しましょう',
    desc: '仕事でかかった費用を記録しておくと、確定申告や収支管理に役立ちます。レシート画像もその場で撮影して添付できます。',
    bullets: ['交通費・機材などカテゴリ別に管理', 'レシートを撮影して添付', '案件に紐づけて収支分析に反映'],
    actionLabel: '経費を記録する',
  },
  invoices: {
    icon: IconInvoices, color: '#a78bfa',
    title: '請求書を作成しましょう',
    desc: '案件と紐づけてワンクリックで請求書を作成し、PDFで出力・送付できます。振込先や署名はプロフィール設定から自動で記載されます。',
    bullets: ['案件・経費とまとめて請求', 'PDFで出力してそのまま送付', 'AIが請求メールの文章を生成'],
    actionLabel: '最初の請求書を作成',
  },
  analytics: {
    icon: IconAnalytics, color: '#34d399',
    title: 'まだ分析できるデータがありません',
    desc: '入金済みの請求書と経費が貯まると、月別の収入・経費・利益や納税概算（約20%）を自動でグラフ化します。',
    bullets: ['月別の収支をグラフで確認', '利益と納税概算を自動計算', 'データが増えるほど精度が上がる'],
    actionLabel: '請求書を作成する',
  },
  ai: {
    icon: IconAi, color: '#c084fc',
    title: 'AIに相談してみましょう',
    desc: 'あなたの案件・収支データをもとに、AIが単価アップ・節税・営業などのアドバイスをします。',
    bullets: ['「今月の収支を分析して」と質問', '確定申告の準備リストを作成', '営業メールの文章作成も得意'],
    actionLabel: '「今月の収支を分析して」と聞く',
  },
  workproof: {
    icon: IconWorkProof, color: '#93c5fd',
    title: '稼働証明書を出力しましょう',
    desc: '複数案件の稼働日をまとめてPDF・Excelで出力できます。電子印（判子）の押印にも対応しています。',
    bullets: ['出力したい案件を選ぶだけ', '月ごとにフィルタリング可能', 'PDF・Excel両形式で出力'],
    actionLabel: null, // 案件を選んで出力する形式なのでボタンなし
  },
};

export default function EmptyState({
  preset,
  icon, color, title, desc, bullets, actionLabel,
  onAction,
}) {
  const { theme } = useTheme();
  const p = (preset && PRESETS[preset]) || {};

  const Icon       = icon       || p.icon       || IconDashboard;
  const accent     = color      || p.color      || theme.accent || '#6366f1';
  const heading    = title      || p.title      || '';
  const body       = desc       || p.desc       || '';
  const tips        = bullets    || p.bullets    || [];
  // actionLabel は明示的に null を渡せばボタン非表示
  const label      = actionLabel !== undefined ? actionLabel : p.actionLabel;
  const showButton = !!label && typeof onAction === 'function';

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
      <div style={{
        width: '100%', maxWidth: 440,
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 18,
        padding: '28px 24px',
        textAlign: 'center',
      }}>
        {/* アイコン */}
        <div style={{
          width: 60, height: 60, borderRadius: 18, margin: '0 auto 16px',
          background: accent + '20',
          border: `1.5px solid ${accent}45`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={30} color={accent} />
        </div>

        {/* タイトル */}
        <div style={{ fontSize: 18, fontWeight: 700, color: theme.textPrimary, marginBottom: 8 }}>
          {heading}
        </div>

        {/* 説明文 */}
        {body && (
          <div style={{ fontSize: 13.5, color: theme.textSecondary, lineHeight: 1.75, marginBottom: tips.length ? 18 : 0 }}>
            {body}
          </div>
        )}

        {/* できることリスト */}
        {tips.length > 0 && (
          <div style={{
            background: theme.bgInput,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: '14px 16px',
            textAlign: 'left',
            display: 'flex', flexDirection: 'column', gap: 9,
            marginBottom: showButton ? 20 : 0,
          }}>
            {tips.map((tip, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: theme.textPrimary, lineHeight: 1.5 }}>
                <span style={{ color: accent, flexShrink: 0, marginTop: 1, fontWeight: 700 }}>✓</span>
                <span>{tip}</span>
              </div>
            ))}
          </div>
        )}

        {/* アクションボタン */}
        {showButton && (
          <button onClick={onAction} style={{
            width: '100%', padding: '13px', borderRadius: 12, border: 'none',
            background: accent, color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', transition: 'opacity 0.15s',
          }}>
            {label}
          </button>
        )}
      </div>
    </div>
  );
}
